import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import _ from "underscore";

const OPERATORS = [
  { value: "equals", label: "is" },
  { value: "in_subtree", label: "is subset of" },
];

const opLabel = (op) =>
  OPERATORS.find((o) => o.value === op)?.label || op;

/**
 * Format a single clause as "Dimension · Operator · Value".
 */
const formatClause = (clause, dimensions) => {
  const dim = dimensions.find((d) => d.id === clause.dimension_id);
  const dimName = dim?.name || clause.dimension_id || "?";
  const operator = opLabel(clause.operator);
  const value = clause.node_label || clause.node_id || "?";
  return `${dimName} \u00b7 ${operator} \u00b7 ${value}`;
};

/**
 * BudgetScopeSummary — read-only scope summary block.
 *
 * Renders two states:
 *  A) Empty: card with "No scope defined yet" + "Define scope" CTA
 *  B) Non-empty: grouped summary with AND/OR semantics
 *     - Single group: clause chips inline (implicit AND)
 *     - Multiple groups: bullet list per group with OR separators
 */
// `bare` drops the card chrome and the heading so this can sit inside a section of a larger
// panel, where the surrounding surface already supplies both. The default keeps the
// standalone card the wizard and the detail page render.
const Shell = ({ bare, children }) =>
  bare ? (
    <div>{children}</div>
  ) : (
    <section className="bg-card border border-card-bd rounded-card p-5">{children}</section>
  );

const BudgetScopeSummary = ({
  scope = { scope_mode: "include", clauses: [] },
  dimensions = [],
  onOpenBuilder,
  isRefreshing = false,
  bare = false,
}) => {
  const clauses = useMemo(
    () => (Array.isArray(scope?.clauses) ? scope.clauses : []),
    [scope]
  );
  const isTotal = scope?.scope_mode === "total" || scope?.total === true;
  const isEmpty = !isTotal && clauses.length === 0;

  // Group by clause_group for display
  const groups = useMemo(() => {
    return _.groupBy(clauses, "clause_group");
  }, [clauses]);
  const groupIds = Object.keys(groups).sort(
    (a, b) => Number.parseInt(a, 10) - Number.parseInt(b, 10)
  );
  const isMultiGroup = groupIds.length > 1;

  if (isTotal) {
    return (
      <Shell bare={bare}>
        {!bare && (
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text uppercase tracking-wide">
              Budget scope
            </h3>
            {onOpenBuilder && (
              <button
                onClick={onOpenBuilder}
                className="text-sm font-medium text-primary hover:text-primary-hover transition-colors cursor-pointer"
              >
                Edit scope
              </button>
            )}
          </div>
        )}
        <div className="flex items-start gap-3">
          {!bare && (
            <div className="w-8 h-8 pt-2 rounded-ctrl bg-surface-2 flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon
                icon={icon({ name: "globe", style: "regular" })}
                className="text-text-2 text-sm"
              />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm text-text">
              Total &mdash; all cloud &amp; AI spend
            </p>
            {/* Saying it twice is padding, not clarity: the label already says "all". */}
            {!bare && (
              <p className="text-xs text-text-2 mt-0.5">
                This budget covers all cloud &amp; AI spend.
              </p>
            )}
          </div>
          {isRefreshing && (
            <span className="text-xs text-text-2">Refreshing…</span>
          )}
        </div>
      </Shell>
    );
  }

  if (isEmpty) {
    return (
      <Shell bare={bare}>
        {!bare && (
          <h3 className="text-sm font-semibold text-text uppercase tracking-wide mb-3">
            Budget scope
          </h3>
        )}
        <div className={bare ? "" : "text-center py-6"}>
          {/* The illustration and the centred block are an empty-state treatment: right for a
              standalone card, far too loud for one row inside a details panel. */}
          {!bare && (
            <div className="mb-2">
              <FontAwesomeIcon
                icon={icon({ name: "layer-group", style: "solid" })}
                className="text-text-3 text-2xl"
              />
            </div>
          )}
          <p className="text-sm font-medium text-text mb-1">
            No scope defined yet
          </p>
          {!bare && (
            <p className="text-xs text-text-2 mb-4 max-w-xs mx-auto">
              Define which organization units, products, or use cases this budget
              applies to.
            </p>
          )}
          {onOpenBuilder &&
            (bare ? (
              <button
                onClick={onOpenBuilder}
                className="text-sm font-medium text-primary hover:text-primary-hover transition-colors cursor-pointer"
              >
                Define scope
              </button>
            ) : (
              <button
                onClick={onOpenBuilder}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded-ctrl transition-colors duration-150 cursor-pointer"
              >
                <FontAwesomeIcon
                  icon={icon({ name: "plus", style: "solid" })}
                  className="text-xs"
                />
                Define scope
              </button>
            ))}
        </div>
      </Shell>
    );
  }

  // Non-empty summary

  return (
    <Shell bare={bare}>
      {bare ? (
        isRefreshing && (
          <p className="text-xs text-text-2 mb-2">Refreshing…</p>
        )
      ) : (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-text uppercase tracking-wide">
              Budget scope
            </h3>
            {isRefreshing && (
              <span className="text-xs text-text-2">Refreshing…</span>
            )}
          </div>
          {onOpenBuilder && (
            <button
              onClick={onOpenBuilder}
              className="text-sm font-medium text-primary hover:text-primary-hover transition-colors cursor-pointer"
            >
              Edit scope
            </button>
          )}
        </div>
      )}

      <div className="space-y-3">
        {/* Single group: show clause chips inline (implicit AND) */}
        {!isMultiGroup &&
          groupIds.map((groupId) => (
            <div key={groupId} className="flex flex-wrap gap-2">
              {groups[groupId].map((clause) => {
                const clauseKey = `${clause.clause_group}-${clause.dimension_id}-${clause.node_id}`;
                return (
                  <span
                    key={clauseKey}
                    className="inline-flex items-center gap-1 text-sm bg-surface-2 border border-border text-text px-2.5 py-1 rounded-ctrl"
                  >
                    {formatClause(clause, dimensions)}
                  </span>
                );
              })}
            </div>
          ))}

        {/* Multiple groups: bullet list with OR separators */}
        {isMultiGroup && (
          <ul className="space-y-2 list-none m-0 p-0">
            {groupIds.map((groupId, gIdx) => {
              const groupClauses = groups[groupId];
              const summary = groupClauses
                .map((c) => formatClause(c, dimensions))
                .join(" AND ");
              return (
                <React.Fragment key={groupId}>
                  <li className="flex items-start gap-2 text-sm text-text">
                    <span className="text-text-2 mt-0.5 flex-shrink-0">
                      &bull;
                    </span>
                    <span>{summary}</span>
                  </li>
                  {gIdx < groupIds.length - 1 && (
                    <li className="flex items-center gap-2" aria-hidden="true">
                      <div className="flex-1 border-t border-border" />
                      <span className="text-xs text-text-2 font-medium uppercase">
                        or
                      </span>
                      <div className="flex-1 border-t border-border" />
                    </li>
                  )}
                </React.Fragment>
              );
            })}
          </ul>
        )}

        {/* Multi-group helper text */}
        {isMultiGroup && (
          <p className="text-xs text-text-2 mt-2">
            Matches costs that satisfy any of the groups above.
          </p>
        )}
      </div>
    </Shell>
  );
};

Shell.propTypes = { bare: PropTypes.bool, children: PropTypes.node };

BudgetScopeSummary.propTypes = {
  scope: PropTypes.shape({
    scope_mode: PropTypes.string,
    total: PropTypes.bool,
    clauses: PropTypes.array,
  }),
  dimensions: PropTypes.array,
  onOpenBuilder: PropTypes.func,
  isRefreshing: PropTypes.bool,
  bare: PropTypes.bool,
};

export default BudgetScopeSummary;
